import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { adminTheme, getColor, getSpacing, getBorderRadius, getShadow } from '../../theme/adminTheme';

const MetricCard = ({
    title,
    value,
    iconName,
    change,
    trend = 'up',
    color = 'primary',
    subtitle
}) => {
    const isPositive = trend === 'up';
    const trendColor = isPositive ? getColor('success.600') : getColor('danger.600');
    const bgColor = getColor(`${color}.50`);
    const iconColor = getColor(`${color}.600`);

    return (
        <View style={[styles.container, getShadow('md')]}>
            <View style={styles.content}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.title}>{title}</Text>
                    {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
                </View>

                {/* Main Value */}
                <View style={styles.valueContainer}>
                    <Text style={styles.value}>{value}</Text>

                    {/* Icon */}
                    <View style={[styles.iconContainer, { backgroundColor: bgColor }]}>
                        <Ionicons name={iconName} size={24} color={iconColor} />
                    </View>
                </View>

                {/* Change Indicator */}
                {change !== undefined && (
                    <View style={styles.changeContainer}>
                        <View style={[styles.changeBadge, { backgroundColor: `${trendColor}15` }]}>
                            <Ionicons
                                name={isPositive ? 'trending-up' : 'trending-down'}
                                size={14}
                                color={trendColor}
                            />
                            <Text style={[styles.changeText, { color: trendColor }]}>
                                {Math.abs(change)}%
                            </Text>
                        </View>
                        <Text style={styles.changeLabel}>vs last month</Text>
                    </View>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
        flex: 1,
        minWidth: 280,
        margin: getSpacing('sm'),
        ...(Platform.OS === 'web' ? {
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            ':hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
            },
        } : {}),
    },
    content: {
        flex: 1,
    },
    header: {
        marginBottom: getSpacing('md'),
    },
    title: {
        fontSize: 14,
        fontWeight: '500',
        color: getColor('gray.600'),
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    subtitle: {
        fontSize: 12,
        color: getColor('gray.500'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    valueContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: getSpacing('md'),
    },
    value: {
        fontSize: 32,
        fontWeight: '700',
        color: getColor('gray.900'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    iconContainer: {
        width: 48,
        height: 48,
        borderRadius: getBorderRadius('lg'),
        justifyContent: 'center',
        alignItems: 'center',
        ...(Platform.OS === 'web' ? {
            transition: 'transform 0.3s ease',
            ':hover': {
                transform: 'rotate(15deg) scale(1.1)',
            },
        } : {}),
    },
    changeContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    changeBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: getSpacing('sm'),
        paddingVertical: 4,
        borderRadius: getBorderRadius('full'),
        marginRight: getSpacing('sm'),
    },
    changeText: {
        fontSize: 12,
        fontWeight: '600',
        marginLeft: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    changeLabel: {
        fontSize: 12,
        color: getColor('gray.500'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
});

export default MetricCard;
